import type { AnalyticsEventProperties, AnalyticsProvider, AnalyticsUserTraits } from "../types";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface GoogleAnalyticsOptions {
  /** GA4 measurement id, e.g. "G-XXXXXXXXXX". */
  measurementId: string;
}

/**
 * GA4 via gtag.js, loaded lazily and only once consent is granted (init() is never called by
 * anything else).
 *
 * `send_page_view: false` is deliberate: this is a client-rendered SPA, and gtag.js's own
 * automatic pageview only ever fires once, on script load — every route change after that would
 * go unmeasured. The router-level tracker calls `page()` explicitly on every navigation instead.
 */
export class GoogleAnalyticsProvider implements AnalyticsProvider {
  readonly name = "google-analytics";
  private loaded = false;

  constructor(private readonly options: GoogleAnalyticsOptions) {}

  init(): void {
    if (this.loaded || typeof document === "undefined") return;
    this.loaded = true;

    window.dataLayer = window.dataLayer ?? [];
    // gtag.js's own bootstrapping snippet — the queue exists so calls made before the script
    // has finished loading aren't lost.
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", this.options.measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.options.measurementId}`;
    document.head.appendChild(script);
  }

  page(path: string, title?: string): void {
    window.gtag?.("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    });
  }

  track(event: string, properties: AnalyticsEventProperties = {}): void {
    window.gtag?.("event", event, properties);
  }

  /** `userId` must be our own internal user id (a UUID), never an email or display name — GA's
   *  terms of service prohibit sending anything that identifies a person outside the product. */
  identify(userId: string, traits: AnalyticsUserTraits = {}): void {
    window.gtag?.("config", this.options.measurementId, { user_id: userId });
    if (Object.keys(traits).length > 0) window.gtag?.("set", "user_properties", traits);
  }

  reset(): void {
    window.gtag?.("config", this.options.measurementId, { user_id: undefined });
  }
}
